import { createScriptLoader } from "@solid-primitives/script-loader";
import { useAuthState } from "./state/auth";
import { type ContextProviderComponent, createContext, createEffect, createMemo, createResource } from "solid-js";
import UserService from "./services/UserService";
import { isNil } from "lodash-es";
import environment from "./environment";
import { initializedContext } from "./utils/initializedUtil";
import { createStore } from "solid-js/store";
import logger from "./utils/logger";

type HubSpotEventListener = ((event: string, fn: (payload: unknown) => void) => void) &
  ((event: "unreadConversationCountChanged", fn: (payload: { unreadCount: number }) => void) => void);

declare global {
  /* oxlint-disable @typescript-eslint/consistent-type-definitions */
  interface Window {
    hsConversationsSettings: Record<string, unknown>;
    hsConversationsOnReady: (() => void)[];
    HubSpotConversations?: {
      widget: {
        load: () => void;
        open: () => void;
        close: () => void;
        status: () => { loaded: boolean; pending: boolean };
        refresh: () => void;
      };
      on: HubSpotEventListener;
    };
  }
}

type State = {
  loaded: boolean;
  status: "open" | "closed";
  unreadMessages: number;
};
type Dispatch = {
  open: () => void;
  close: () => void;
};

const StateContext = createContext<State>();
const DispatchContext = createContext<Dispatch>();

export const HubSpotProvider: ContextProviderComponent<undefined> = (props) => {
  const { loginStatus } = useAuthState();
  const [user, { refetch }] = createResource(() => {
    if (loginStatus() === "loggedIn") {
      return UserService.getUser();
    }
  });
  const username = createMemo(() => user()?.data?.username);

  const getHubSpotConversations = () => {
    if (isNil(window.HubSpotConversations)) {
      logger.warn("'HubSpotConversations' is not available on window");
      return;
    }

    return window.HubSpotConversations;
  };
  const getHubSpotIFrameContainer = () => {
    const container = document.getElementById("hubspot-messages-iframe-container");
    if (isNil(container)) {
      throw new Error("HubSpot messages iframe container is not present in DOM?");
    }

    return container;
  };

  createEffect(() => {
    if (loginStatus() === "loggedIn") {
      refetch();
    }
  });

  createEffect(() => {
    if (loginStatus() === "loggedIn" && !isNil(username()) && environment.isProduction()) {
      window.hsConversationsSettings.identificationEmail = username();
      UserService.getVisitorToken().then((token) => {
        window.hsConversationsSettings.identificationToken = token.token;
        getHubSpotConversations()?.widget.refresh(); // Refresh the widget to apply the identification
      });
    }
  });

  window.hsConversationsSettings = {
    loadImmediately: false,
    enableWidgetCookieBanner: true,
  };

  const [store, setStore] = createStore<State>({
    loaded: false,
    status: "closed",
    unreadMessages: 0,
  });

  const dispatch = {
    open: () => {
      getHubSpotConversations()?.widget.open();
      getHubSpotIFrameContainer().style.visibility = "visible";
      setStore("status", "open");
    },
    close: () => {
      getHubSpotConversations()?.widget.close();
      getHubSpotIFrameContainer().style.visibility = "hidden";
      setStore("status", "closed");
    },
  };

  createScriptLoader({
    id: "hs-script-loader",
    type: "text/javascript",
    src: "https://js-eu1.hs-scripts.com/140494897.js",
    async: true,
    defer: true,
    onLoad: async () => {
      window.hsConversationsOnReady = [
        () => {
          const hubSpotConversations = getHubSpotConversations();
          if (!isNil(hubSpotConversations)) {
            hubSpotConversations.widget.load();
            hubSpotConversations.on("widgetLoaded", () => {
              setStore("loaded", true);
            });
            hubSpotConversations.on("widgetClosed", () => {
              dispatch.close();
            });
            hubSpotConversations.on("unreadConversationCountChanged", (payload) => {
              setStore("unreadMessages", payload.unreadCount);
            });
          }
        },
      ];
    },
  });

  return (
    <StateContext.Provider value={store}>
      <DispatchContext.Provider value={dispatch}> {props.children} </DispatchContext.Provider>
    </StateContext.Provider>
  );
};

export const useHubSpotState = () => initializedContext(StateContext, "useHubSpotState", "HubSpotProvider");
export const useHubSpotDispatch = () => initializedContext(DispatchContext, "useHubSpotDispatch", "HubSpotProvider");
